// Error Recorder — auto-captures failed API calls (esp. PAID generation tools)
// and uncaught JS errors, classifies severity, keeps a 48h timestamped log in
// localStorage, and builds a copy-paste report for the founder Admin panel.

const STORE_KEY = "mist_error_log_v1";
const MAX_ENTRIES = 80;
const RETENTION_MS = 48 * 60 * 60 * 1000; // 48 hours

// Endpoints that cost money / power paid generation tools. A failure here is
// something to WORRY about.
const GEN_PATTERNS = [
  "/pipelines/",
  "/mist-creations/invoke",
  "/mist-creations/reel",
  "/mist-creations/song/cover/generate",
  "/music/lyrics",
  "/music/produce",
  "/voice/synthesize",
  "/voice/poem-audio",
  "/alix/chat",
  "/alix/generate-creative",
  "/alix/vision",
  "/central-core/",
  "/imagery/hub",
  "/video/hub",
  "/writing/hub",
  "/blueprint/render",
  "/blueprint/animate",
];

function nowEnv() {
  const h = (typeof window !== "undefined" && window.location.hostname) || "";
  return h.includes("emergent.host") ? "PRODUCTION" : "PREVIEW";
}

function isGenerationUrl(url = "") {
  return GEN_PATTERNS.some((p) => url.includes(p));
}

function read() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const cutoff = Date.now() - RETENTION_MS;
    // 48h auto-clear: drop anything older than the window.
    return arr.filter((e) => e && e.tsMs && e.tsMs >= cutoff);
  } catch {
    return [];
  }
}

function write(arr) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(arr.slice(-MAX_ENTRIES)));
  } catch {
    /* storage full / unavailable — ignore */
  }
}

// Decide how much the founder should worry about an entry.
function classify({ kind, gen, status }) {
  if (kind === "js") return "warning";
  if (gen) {
    if (!status || status >= 500 || status === 402 || status === 429) return "critical";
    return "warning"; // 4xx on a paid tool — bad input / auth / gate
  }
  // Non-paid endpoints.
  if (!status || status >= 500) return "warning";
  if (status === 401 || status === 403 || status === 404) return "ignore";
  return "ignore";
}

const EXPLAIN = {
  critical: "⚠️ WORRY — a paid generation tool failed for the user.",
  warning: "🟡 Keep an eye on this.",
  ignore: "⚪ Safe to ignore — routine / expected.",
};

export function recordApiError(error) {
  try {
    const cfg = error?.config || {};
    const res = error?.response;
    const base = cfg.baseURL || "";
    let url = cfg.url || "";
    if (base && url.startsWith(base)) url = url.slice(base.length);
    const status = res?.status || 0;
    const gen = isGenerationUrl(url);
    const detail =
      (res?.data && (res.data.detail || res.data.message || res.data.error)) ||
      error?.message ||
      "Request failed";
    const severity = classify({ kind: "api", gen, status });
    push({
      kind: "api",
      gen,
      method: (cfg.method || "get").toUpperCase(),
      url,
      status,
      message: typeof detail === "string" ? detail.slice(0, 300) : JSON.stringify(detail).slice(0, 300),
      severity,
    });
  } catch {
    /* never let the recorder throw */
  }
}

function recordJsError(message, source = "") {
  push({
    kind: "js",
    gen: false,
    method: "JS",
    url: source,
    status: 0,
    message: String(message).slice(0, 300),
    severity: "warning",
  });
}

function push(entry) {
  const tsMs = Date.now();
  const full = { id: `${tsMs}-${Math.random().toString(36).slice(2, 7)}`, tsMs, ts: new Date(tsMs).toISOString(), env: nowEnv(), ...entry };
  const arr = read();
  arr.push(full);
  write(arr);
  // Let the floating badge (and any open admin panel) react instantly.
  try { window.dispatchEvent(new CustomEvent("mist:error-captured", { detail: full })); } catch { /* ignore */ }
}

const BADGE_KEY = "mist_error_badge_enabled";
export function isBadgeEnabled() {
  try { return localStorage.getItem(BADGE_KEY) === "1"; } catch { return false; }
}
export function setBadgeEnabled(on) {
  try { localStorage.setItem(BADGE_KEY, on ? "1" : "0"); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("mist:error-badge-toggle", { detail: !!on })); } catch { /* ignore */ }
}

export function getErrors() {
  // Returns newest-first, critical bubbled to the top.
  const arr = read();
  const rank = { critical: 0, warning: 1, ignore: 2 };
  return arr
    .slice()
    .sort((a, b) => (rank[a.severity] - rank[b.severity]) || (b.tsMs - a.tsMs));
}

export function getCounts() {
  const arr = read();
  return {
    critical: arr.filter((e) => e.severity === "critical").length,
    warning: arr.filter((e) => e.severity === "warning").length,
    ignore: arr.filter((e) => e.severity === "ignore").length,
    total: arr.length,
  };
}

export function clearErrors() {
  try {
    localStorage.removeItem(STORE_KEY);
  } catch {
    /* ignore */
  }
}

// Build a formatted, copy-paste report (optionally merged with backend data).
export function buildReport({ health, serverErrors } = {}) {
  const errs = getErrors();
  const c = getCounts();
  const lines = [];
  lines.push("========== MiSt GENERATION ERROR REPORT ==========");
  lines.push(`Generated: ${new Date().toISOString()}`);
  lines.push(`Environment: ${nowEnv()}`);
  lines.push(`Retention: last 48h`);
  if (health) lines.push(`Health: ${health.passed}/${health.total} checks passed${health.failed ? ` (${health.failed} FAILED)` : ""}`);
  lines.push(`Errors: ${c.critical} critical · ${c.warning} warning · ${c.ignore} routine`);
  lines.push("");

  if (health && Array.isArray(health.checks)) {
    const failed = health.checks.filter((x) => x.status === "fail");
    if (failed.length) {
      lines.push("--- FAILED HEALTH CHECKS ---");
      failed.forEach((x) => lines.push(`  ✗ [${x.group}] ${x.name}: ${x.detail}`));
      lines.push("");
    }
  }

  if (errs.length) {
    lines.push("--- CLIENT-SIDE ERRORS (user's browser) ---");
    errs.forEach((e) => {
      const tag = e.severity === "critical" ? "[CRITICAL]" : e.severity === "warning" ? "[WARNING]" : "[routine]";
      lines.push(`${tag} ${e.ts} | ${e.method} ${e.url || "(js)"} | ${e.status || "-"}`);
      lines.push(`    ${e.message}`);
    });
    lines.push("");
  } else {
    lines.push("(no client-side errors captured in the last 48h)");
    lines.push("");
  }

  if (Array.isArray(serverErrors) && serverErrors.length) {
    lines.push("--- SERVER LOG ERRORS ---");
    serverErrors.forEach((s) => lines.push(`${s.severity === "critical" ? "[CRITICAL]" : "[warn]"} ${s.line}`));
    lines.push("");
  }

  lines.push("==================================================");
  return lines.join("\n");
}

export { EXPLAIN, RETENTION_MS };

let _installed = false;
export function initErrorRecorder(apiInstance) {
  if (_installed) return;
  _installed = true;
  // Axios interceptor on the shared api instance — captures every failed call.
  if (apiInstance?.interceptors) {
    apiInstance.interceptors.response.use(
      (r) => r,
      (error) => {
        recordApiError(error);
        // Casual PAYG gate: a 402 top_up_required from any generation call opens
        // the Stripe top-up modal globally (see TopUpGate). Non-fatal if it fails.
        try {
          const st = error?.response?.status;
          const det = error?.response?.data?.detail;
          if (st === 402 && (det?.error === "top_up_required" || det === "top_up_required")) {
            window.dispatchEvent(new CustomEvent("mist:topup-required", { detail: det }));
          }
        } catch (_) { /* ignore */ }
        return Promise.reject(error);
      },
    );
  }
  // Uncaught JS errors + promise rejections.
  if (typeof window !== "undefined") {
    window.addEventListener("error", (e) => recordJsError(e?.message || "window error", e?.filename || ""));
    window.addEventListener("unhandledrejection", (e) =>
      recordJsError(e?.reason?.message || e?.reason || "unhandled promise rejection", "promise"),
    );
  }
  // Prune the 48h window at startup.
  write(read());
}
